import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const TOPICS = [
  { key: 'communication', label: 'Communication before your tour' },
  { key: 'hotel', label: 'Hotel / accommodation' },
  { key: 'food', label: 'Food' },
  { key: 'activities', label: 'Activities' },
  { key: 'sessions', label: 'Training sessions' },
  { key: 'fixtures', label: 'Fixtures' },
  { key: 'transport', label: 'Transport' },
  { key: 'tourLeader', label: 'Your tour leader' },
  { key: 'overall', label: 'Overall, how likely are you to recommend us to a friend or colleague?' },
];

function Card({ children, wide }) {
  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4 py-10">
      <div className={`bg-white rounded-[12px] border border-[#E5E7EB] p-8 w-full ${wide ? 'max-w-lg' : 'max-w-md text-center'}`}>
        {children}
      </div>
    </div>
  );
}

function NpsPicker({ value, onChange, brandColor }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {Array.from({ length: 11 }, (_, i) => i).map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={`w-9 h-9 rounded-[8px] text-[13px] font-semibold border transition ${
            value === n ? 'text-white border-transparent' : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#D1D5DB]'
          }`}
          style={value === n ? { backgroundColor: brandColor } : undefined}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

export default function SurveyRespond() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [phase, setPhase] = useState('loading'); // loading | invalid | form | submitted | error
  const [requestId, setRequestId] = useState(null);
  const [tour, setTour] = useState(null); // { tourName, contactName, brand }
  const [scores, setScores] = useState({});
  const [comments, setComments] = useState({});
  const [otherFeedback, setOtherFeedback] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!token) { setPhase('invalid'); return; }

    async function init() {
      try {
        const tokenSnap = await getDoc(doc(db, 'surveyTokens', token));
        if (!tokenSnap.exists()) { setPhase('invalid'); return; }
        const { requestId: rid } = tokenSnap.data();

        const responseSnap = await getDoc(doc(db, 'requests', rid, 'survey', 'response'));
        if (!responseSnap.exists()) { setPhase('invalid'); return; }
        const data = responseSnap.data();

        setRequestId(rid);
        setTour({ tourName: data.tourName, contactName: data.contactName, brand: data.brand });
        setPhase(data.status === 'completed' ? 'submitted' : 'form');
      } catch (err) {
        console.error(err);
        setPhase('error');
      }
    }

    init();
  }, [token]);

  function setScore(key, value) {
    setScores((prev) => ({ ...prev, [key]: value }));
  }
  function setComment(key, value) {
    setComments((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError('');
    if (TOPICS.some((t) => scores[t.key] === undefined)) {
      setFormError('Please rate every topic before submitting.');
      return;
    }
    setSaving(true);
    try {
      await updateDoc(doc(db, 'requests', requestId, 'survey', 'response'), {
        status: 'completed',
        respondedAt: serverTimestamp(),
        scores,
        comments,
        otherFeedback,
      });
      setPhase('submitted');
    } catch (err) {
      console.error(err);
      setFormError('Something went wrong. Please try again or email padelante@mstsportstours.com.');
    } finally {
      setSaving(false);
    }
  }

  const isYourTour = tour?.brand === 'your_tour';
  const isPadelante = tour?.brand === 'padelante';
  const brandColor = isYourTour ? '#00529B' : isPadelante ? '#1B4079' : '#1A4A8A';
  const headerColor = isYourTour ? '#00529B' : isPadelante ? '#1B4079' : '#0D2240';
  const companyName = isYourTour ? 'Your Tour' : isPadelante ? 'Padelante' : 'MST Sports Tours';

  if (phase === 'loading') {
    return <Card><p className="text-[#9CA3AF] text-[14px]">Loading…</p></Card>;
  }

  if (phase === 'invalid' || phase === 'error') {
    return (
      <Card>
        <p className="text-[15px] font-semibold text-[#111827] mb-2">
          {phase === 'invalid' ? 'This link is invalid or has expired.' : 'Something went wrong.'}
        </p>
        <p className="text-[13px] text-[#6B7280]">
          If you think this is an error, email padelante@mstsportstours.com.
        </p>
      </Card>
    );
  }

  if (phase === 'submitted') {
    return (
      <Card>
        <div className="w-12 h-12 bg-[#ECFDF5] text-[#1A7A4A] rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="text-[17px] font-bold text-[#111827] mb-2">Thanks for your feedback!</p>
        <p className="text-[13px] text-[#6B7280]">
          We really appreciate you taking the time — it helps us improve every tour.
        </p>
      </Card>
    );
  }

  return (
    <Card wide>
      <div className="rounded-[10px] p-4 mb-6 text-center" style={{ backgroundColor: headerColor }}>
        <p className="text-white font-bold text-[15px]">{companyName}</p>
      </div>
      <h2 className="text-[16px] font-bold text-[#111827] mb-1">
        Hi {tour?.contactName || 'there'}, how was your tour?
      </h2>
      <p className="text-[13px] text-[#6B7280] mb-6">
        {tour?.tourName ? `${tour.tourName} — ` : ''}On a scale of 0–10, how would you rate each of the following?
      </p>
      <form onSubmit={handleSubmit} className="space-y-6">
        {TOPICS.map((topic) => (
          <div key={topic.key}>
            <label className="block text-[13px] font-medium text-[#374151] mb-2">{topic.label}</label>
            <NpsPicker value={scores[topic.key]} onChange={(v) => setScore(topic.key, v)} brandColor={brandColor} />
            <input
              type="text"
              placeholder="Anything you'd like to add? (optional)"
              value={comments[topic.key] || ''}
              onChange={(e) => setComment(topic.key, e.target.value)}
              className="w-full mt-2 border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
            />
          </div>
        ))}
        <div>
          <label className="block text-[13px] font-medium text-[#374151] mb-2">Anything else you'd like to share?</label>
          <textarea
            value={otherFeedback}
            onChange={(e) => setOtherFeedback(e.target.value)}
            rows={3}
            className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
          />
        </div>
        {formError && (
          <div className="bg-red-50 border border-red-200 rounded-[8px] p-3 text-[13px] text-red-700">{formError}</div>
        )}
        <button
          type="submit"
          disabled={saving}
          className="w-full py-3 text-white font-bold text-[14px] rounded-[10px] transition disabled:opacity-60"
          style={{ backgroundColor: brandColor }}
        >
          {saving ? 'Submitting…' : 'Submit feedback'}
        </button>
      </form>
    </Card>
  );
}
